/*!
 * @copyright
 * Copyright(c) 2019 SICK AG
 */
import { Box3, BoxBufferGeometry, EdgesGeometry, LineBasicMaterial, LineSegments, Object3D, Raycaster, Vector3, BackSide, Quaternion, } from 'three';
import { Events } from '../../Events';
import { FunctionalActor } from '../../../actors/FunctionalActor';
import { BOUNDINGBOX_EDITOR_TYPE_NAME, DEFAULT_BOUNDINGBOX_EDITOR_NAME } from './constants';
import { BoundingBoxOperation, SegmentPlane } from './SegmentPlane';
import { AbstractEditor } from '../AbstractEditor';
import { GizmoMode, Operation, OperationDOF } from '../GizmoKit/GizmoKit';
import { GizmoKitContext } from '../GizmoKit/GizmoKitContext';
import { MousePosUtils } from '../../MousePosUtils';
var EditorState;
(function (EditorState) {
    EditorState[EditorState["NeedsObject"] = 0] = "NeedsObject";
    EditorState[EditorState["Idle"] = 1] = "Idle";
    EditorState[EditorState["Translate"] = 2] = "Translate";
    EditorState[EditorState["ScaleTranslate"] = 3] = "ScaleTranslate";
})(EditorState || (EditorState = {}));
/**
 * Check if the object is a bounding box editor or is inside a shape editor object hierarchy.
 * @param object a 3d object
 */
const isBoundingBoxEditor = (object) => {
    let obj = object;
    while (obj) {
        if (obj.type === BOUNDINGBOX_EDITOR_TYPE_NAME) {
            return true;
        }
        obj = obj.parent;
    }
    return false;
};
/**
 * BoundingBox based Editor
 */
export class BoundingBoxEditor extends AbstractEditor {
    // private _startPosition: Vector3;
    constructor(stage, options) {
        super(stage, options);
        this._state = EditorState.NeedsObject;
        this._transformContext = new GizmoKitContext();
        this._raycaster = new Raycaster();
        this._boundingBox = new Box3();
        this._box = new Object3D();
        this._segmentsContainer = new Object3D();
        this._lastIntersections = new Array();
        this._interactiveObjects = new Array();
        this._xyRotationVector = new Vector3(0, 0, 0);
        this._zxRotationVector = new Vector3(1, 0, 0);
        this._yzRotationVector = new Vector3(0, 1, 0);
        this._onCanvasMouseDown = (event) => {
            this._mousePosition = MousePosUtils.readRelMousePos(event, this._stageDomElement);
            if (this._state === EditorState.Idle) {
                const intersection = this._raycast(this._mousePosition)[0];
                if (intersection) {
                    intersection.object.dispatchEvent({ type: 'click', detail: { intersection, editor: this } });
                }
            }
        };
        this._onCanvasMouseUp = () => {
            if (this._state === EditorState.Translate || this._state === EditorState.ScaleTranslate) {
                this._updateBoxMesh();
                this._deActivateCurrentTool();
            }
        };
        this._onCanvasMouseMove = (event) => {
            this._mousePosition = MousePosUtils.readRelMousePos(event, this._stageDomElement);
            switch (this._state) {
                case EditorState.Translate:
                    this._translate(this._mousePosition);
                    break;
                case EditorState.ScaleTranslate:
                    this._scaleTranslate(this._mousePosition);
                    break;
                default:
                // nothing to do here
            }
        };
        this.type = BOUNDINGBOX_EDITOR_TYPE_NAME;
        this.name = options && options.name || DEFAULT_BOUNDINGBOX_EDITOR_NAME;
        this._createBox();
    }
    undo() {
        super.undo();
    }
    redo() {
        super.redo();
    }
    updateForObject(object) {
        this._object = object;
        this._updateBoxMesh();
    }
    setGizmoMode(gizmoMode) {
        // at the moment GizmoMode.View is not supported (and doesn't make much sense with BoundingBoxEditor)
        if (gizmoMode !== GizmoMode.View) {
            this._gizmoKit.setMode(gizmoMode);
            this._updateBoxMesh();
        }
    }
    clearObject() {
        this._unRegisterEventListeners();
        const old = this._object;
        this._object = null;
        this._state = EditorState.NeedsObject;
        this._stage.applyThreeDirect((scene) => { scene.remove(this); });
        this._transformContext = null;
        this._gizmoKit.setObject(null);
        this.dispatchEvent(new CustomEvent(// TODO: into the gizmoKit ???
        Events.GIZMOCHANGED, {
            detail: {
                old,
                new: null,
                editor: this,
            },
        }));
    }
    switchToObject(object) {
        if (isBoundingBoxEditor(object)) {
            // tslint:disable-next-line:no-console
            console.warn('BoundingBoxEditor: can not install bounding-box-editor to a bounding-box-editor hierarchy!');
            return;
        }
        this.add(this._box);
        const old = this._object;
        this._object = object;
        this._state = object == null ? EditorState.NeedsObject : EditorState.Idle;
        this._gizmoKit.setObject(object);
        this._gizmoKit.setCamera(this._camera);
        this._stage.applyThreeDirect((scene) => { scene.remove(this); });
        this._stage.applyThreeDirect((scene) => { scene.add(this); });
        this._registerEventListeners();
        this._updateBoxMesh();
        this.dispatchEvent(new CustomEvent(// TODO: into the gizmoKit ???
        Events.GIZMOCHANGED, {
            detail: {
                old,
                new: this._object,
                editor: this,
            },
        }));
    }
    _activate(event) {
        // this._deActivateCurrentTool();
        console.trace();
        this._operationVector = event.detail.operationVector;
        this._rotationAxis = event.detail.rotationAxis;
        this._state = event.detail.operation === BoundingBoxOperation.Translate ? EditorState.Translate : EditorState.ScaleTranslate;
        this._stage.pauseNavigator(true);
        this._transformContext = this._transformContext || new GizmoKitContext();
        this._transformContext.init(this._getOperation(event.detail.operation), this._getDof());
        /*this._startPosition = new Vector3();
        this._object.matrixWorld.decompose(this._startPosition, new Quaternion(), new Vector3());*/
        // this._transformContext.
        if (this._lastIntersections[0] != null) {
            this._transformContext.surfaceCenter = this._lastIntersections[0].point.clone();
        }
        this._gizmoKit.activate(this._transformContext, this._mousePosition);
    }
    _deActivateCurrentTool() {
        this._gizmoKit.deactivate();
        this._state = EditorState.Idle;
        this._stage.pauseNavigator(false);
    }
    _registerEventListeners() {
        this._stageDomElement.addEventListener('mousemove', this._onCanvasMouseMove);
        this._stageDomElement.addEventListener('mousedown', this._onCanvasMouseDown);
        this._stageDomElement.addEventListener('mouseup', this._onCanvasMouseUp);
        this._stage.addActor(new FunctionalActor(() => {
            this._updateHighlight();
        }));
    }
    _unRegisterEventListeners() {
        this._stageDomElement.removeEventListener('mousemove', this._onCanvasMouseMove);
        this._stageDomElement.removeEventListener('mousedown', this._onCanvasMouseDown);
        this._stageDomElement.removeEventListener('mouseup', this._onCanvasMouseUp);
    }
    _raycast(pos = this._mousePosition) {
        if (this._mousePosition == null) {
            return [];
        }
        this._raycaster.setFromCamera({ x: pos.mousex, y: pos.mousey }, this._camera);
        return this._raycaster.intersectObjects(this._interactiveObjects, true);
    }
    _translate(pos) {
        this._gizmoKit.translate(this._transformContext, pos);
        this._updateBoxMesh();
    }
    _scaleTranslate(pos) {
        this._gizmoKit.scale(this._transformContext, pos, this._operationVector);
        this._updateBoxMesh();
    }
    _getDof(operationVector = this._operationVector, rotationAxis = this._rotationAxis) {
        const hasX = operationVector.x !== 0;
        const hasY = operationVector.y !== 0;
        const hasZ = operationVector.z !== 0;
        if (hasX && hasY) {
            return OperationDOF.XY;
        }
        if (hasZ && hasX) {
            return OperationDOF.ZX;
        }
        if (hasY && hasZ) {
            return OperationDOF.YZ;
        }
        if (hasX) {
            return OperationDOF.X;
        }
        if (hasY) {
            return OperationDOF.Y;
        }
        if (hasZ) {
            return OperationDOF.Z;
        }
        // there is one special case: the centered SegmentParts have a zero operationVector
        // therefore detect the dof with the rotationVector of the SegmentPlanes
        if (rotationAxis.equals(this._xyRotationVector)) {
            return OperationDOF.XY;
        }
        if (rotationAxis.equals(this._zxRotationVector)) {
            return OperationDOF.ZX;
        }
        if (rotationAxis.equals(this._yzRotationVector)) {
            return OperationDOF.YZ;
        }
        return OperationDOF.NON;
    }
    _getOperation(operation) {
        return operation === BoundingBoxOperation.Translate ? Operation.Translate : Operation.Scale;
    }
    _createBox() {
        // create 6 planes for the bounding box cube
        this._xyPositiveZ = new SegmentPlane(this._xyRotationVector, 0);
        this._xyNegativeZ = new SegmentPlane(this._xyRotationVector, 0, BackSide);
        this._zxPositiveY = new SegmentPlane(this._zxRotationVector, -Math.PI / 2);
        this._zxNegativeY = new SegmentPlane(this._zxRotationVector, Math.PI / 2);
        this._yzPositiveX = new SegmentPlane(this._yzRotationVector, Math.PI / 2);
        this._yzNegativeX = new SegmentPlane(this._yzRotationVector, -Math.PI / 2);
        this._interactiveObjects.push(this._xyPositiveZ, this._xyNegativeZ, this._zxPositiveY, this._zxNegativeY, this._yzPositiveX, this._yzNegativeX);
        this._interactiveObjects.forEach((obj) => obj.addEventListener('click', (event) => this._activate(event)));
        this._segmentsContainer.add(...this._interactiveObjects);
        const geometry = new EdgesGeometry(new BoxBufferGeometry(1, 1, 1));
        this._outline = new LineSegments(geometry, new LineBasicMaterial({ color: 0 }));
        this._box.add(this._segmentsContainer);
        this._box.add(this._outline);
    }
    _updateBoxMesh() {
        if (this._gizmoKit.getMode() === GizmoMode.Local) {
            // calculate BoundingBox from position array to ensure
            // the BoundingBox is aligned to the local coordination system of the object
            // boundingBox.setFromObject(..) is always a world axis aligned bounding box
            // furthermore apply the scale vector from the matrixWorld to ensure the bounding box
            // is also calculated correctly for parent/child objects
            const scale = new Vector3();
            this._object.matrixWorld.decompose(new Vector3(), new Quaternion(), scale);
            // tslint:disable-next-line:no-any
            const positions = this._object.geometry.attributes.position.array;
            const newPositions = new Array(positions.length);
            for (let i = 0; i < positions.length; i += 3) {
                const vertice = new Vector3().fromArray(positions, i);
                vertice.multiply(scale);
                newPositions[i + 0] = vertice.x;
                newPositions[i + 1] = vertice.y;
                newPositions[i + 2] = vertice.z;
            }
            this._boundingBox.setFromArray(newPositions);
            // ensure the box is centered / can be different if previously GizmoMode.Global was set
            this._box.position.set(0, 0, 0);
        }
        else {
            this._boundingBox.setFromObject(this._object);
            // correct center of the box mesh for parent/child objects
            const center = this._boundingBox.getCenter(new Vector3()).sub(this.position);
            this._box.position.set(center.x, center.y, center.z);
        }
        if (this._boundingBox.isEmpty()) {
            return;
        }
        const boxSize = this._getBoundingBoxSize();
        const ratioXY = Math.min(boxSize.x, boxSize.y) / Math.max(boxSize.x, boxSize.y);
        const ratioXZ = Math.min(boxSize.x, boxSize.z) / Math.max(boxSize.x, boxSize.z);
        const ratioYZ = Math.min(boxSize.z, boxSize.y) / Math.max(boxSize.z, boxSize.y);
        // xyPositiveZ
        this._xyPositiveZ.position.setZ(boxSize.z / 2);
        this._xyPositiveZ.scale.set(boxSize.x, boxSize.y, 1);
        this._xyPositiveZ.setRatio(ratioXY, boxSize.x < boxSize.y ? 'y' : 'x');
        // xyNegativeZ
        this._xyNegativeZ.position.setZ(-boxSize.z / 2);
        this._xyNegativeZ.scale.set(boxSize.x, boxSize.y, 1);
        this._xyNegativeZ.setRatio(ratioXY, boxSize.x < boxSize.y ? 'y' : 'x');
        // xzPositiveY
        this._zxPositiveY.position.setY(boxSize.y / 2);
        this._zxPositiveY.scale.set(boxSize.x, boxSize.z, 1);
        this._zxPositiveY.setRatio(ratioXZ, boxSize.x < boxSize.z ? 'y' : 'x');
        // xzNegativeY
        this._zxNegativeY.position.setY(-boxSize.y / 2);
        this._zxNegativeY.scale.set(boxSize.x, boxSize.z, 1);
        this._zxNegativeY.setRatio(ratioXZ, boxSize.x < boxSize.z ? 'y' : 'x');
        // yzPositiveX
        this._yzPositiveX.position.setX(boxSize.x / 2);
        this._yzPositiveX.scale.set(boxSize.z, boxSize.y, 1);
        this._yzPositiveX.setRatio(ratioYZ, boxSize.z < boxSize.y ? 'y' : 'x');
        // yzNegativeX
        this._yzNegativeX.position.setX(-boxSize.x / 2);
        this._yzNegativeX.scale.set(boxSize.z, boxSize.y, 1);
        this._yzNegativeX.setRatio(ratioYZ, boxSize.z < boxSize.y ? 'y' : 'x');
        this._outline.scale.set(boxSize.x, boxSize.y, boxSize.z);
    }
    _getBoundingBoxSize() {
        const min = this._boundingBox.min;
        const max = this._boundingBox.max;
        const sizeX = max.x - min.x;
        const sizeY = max.y - min.y;
        const sizeZ = max.z - min.z;
        return new Vector3(sizeX, sizeY, sizeZ);
    }
    _updateHighlight() {
        if (this._state !== EditorState.Translate && this._state !== EditorState.ScaleTranslate) {
            const intersections = this._raycast();
            const highLightElements = intersections.filter((element) => !this._lastIntersections.find((existing) => existing.object === element.object));
            const unHighlighElements = this._lastIntersections.filter((element) => !intersections.find((existing) => existing.object === element.object));
            const updateHighlight = (part, visible) => {
                if (!this._gizmoKit.lockedFor(this._getOperation(part.operation), this._getDof(part.operationVector, part.rotationAxis))) {
                    part.material.visible = visible;
                }
            };
            highLightElements.forEach((element) => updateHighlight(element.object, true));
            unHighlighElements.forEach((element) => updateHighlight(element.object, false));
            this._lastIntersections = intersections;
        }
    }
}
//# sourceMappingURL=BoundingBoxEditor.js.map